import * as React from 'react';
import { WalletContext, WalletContextProps } from '../../../contexts/wallet/WalletContext';
import { CreateWalletStore } from '../../../contexts/wallet/store/WalletStore';
import { useServiceResolver } from '@services/useServiceResolver';
import { ResponseCode, WalletScaRequest } from '@ul/api';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';
import { WalletViewModel } from '@apis/model/wallet/WalletViewModel';
import { usePushNotification } from '@components/Notification/useNotification';
import { useNavHistory } from '@contexts/navigation/hooks/useNavHistory';
import { useTranslation } from '@contexts/translation/hooks/useTranslation';
import { ScaPanelCmsWithDefault } from '../ScaPanelCmsWithDefault';
import { buildRequestBlockedMessage } from '@v2/utils/wallet/blockMessage';
import { handlePasskeyAuthResponse, PushNotificationFn } from '@v2/utils/wallet/passkeyResponseHandler';

export function WithWalletScaContext<TProps>(Component: React.FC<TProps>): React.FC<React.PropsWithRef<TProps>> {
    const MemoizedComponent = React.memo<React.FC<TProps>>(Component);

    const ComponentWithWalletAuthContext: React.FC<React.PropsWithRef<TProps>> = props => {
        const [walletStorage] = React.useState(() => CreateWalletStore({ walletAuthState: 'loading' }));
        const { loginService, sdkEffect } = useServiceResolver();

        const { goTo: goToResetPin } = useNavHistory('walletResetPin');
        const { goTo: goToRegisterPasskey } = useNavHistory('registerPasskey');

        const translation = useTranslation(ScaPanelCmsWithDefault);

        const pushNotification = usePushNotification('WalletSca');

        const { setWalletAuthState, setOtpVm, setIsBlocked } = useStore(
            walletStorage,
            useShallow(state => ({
                setWalletAuthState: state.setWalletAuthState,
                setOtpVm: state.setOtpVm,
                setIsBlocked: state.setIsBlocked,
            }))
        );

        const getTransactionId = () => {
            return walletStorage.getState().transactionInfo?.transactionId;
        };

        const getMfaOperationCode = () => {
            return walletStorage.getState().transactionInfo?.mfaOperationCode;
        };

        const getScaContext = () => ({
            amount: walletStorage.getState().transactionInfo?.amount,
            currency: walletStorage.getState().transactionInfo?.currency,
        });

        const passkeyCallbackRef = React.useRef<(() => Promise<void>) | undefined>(undefined);

        const onSubmit = async (request: Partial<WalletScaRequest>, onPasskeyCallback?: () => Promise<void>) => {
            passkeyCallbackRef.current = onPasskeyCallback;
            const response = await loginService.scaAuthen(
                { ...request, mfaOperation: getMfaOperationCode(), scaContext: getScaContext() },
                getTransactionId()
            );
            await handleResponseCodeDefault(response);
        };

        const onPinSubmit = async (pin: string) => {
            const response = await loginService.scaAuthen(
                { pin, scaContext: getScaContext(), mfaOperation: getMfaOperationCode() },
                getTransactionId()
            );
            return handleResponseCodeDefault(response);
        };

        const onResendOtp = async () => {
            const response = await loginService.scaAuthen(
                { scaContext: getScaContext(), pin: walletStorage.getState().pin, mfaOperation: getMfaOperationCode() },
                getTransactionId()
            );
            await handleResponseCodeDefault(response);
        };

        const handleResponseCodeDefault = async (response: WalletViewModel) => {
            switch (response.responseCode) {
                case ResponseCode.NeedOtp: {
                    setOtpVm(response.otpViewModel);
                    setWalletAuthState('otp');
                    break;
                }
                case ResponseCode.NeedPinInput: {
                    setWalletAuthState('pin');
                    break;
                }
                case ResponseCode.NeedPinSetup: {
                    goToResetPin();
                    break;
                }
                case ResponseCode.NeedPasskeyRegistration: {
                    goToRegisterPasskey();
                    break;
                }
                case ResponseCode.Successful: {
                    sdkEffect.onWalletAuthenticated(true);
                    break;
                }
                case ResponseCode.UserHasPasskey: {
                    if (passkeyCallbackRef.current) {
                        await passkeyCallbackRef.current();
                    }
                    break;
                }
                case ResponseCode.OtpVerificationFailed: {
                    pushNotification({
                        color: 'error',
                        message: translation.InvalidOtp,
                        testId: 'invalid-otp-code',
                        overrideIconId: 'fill.symbol.triangle-exclamation',
                    });
                    return false;
                }
                case ResponseCode.OtpVerificationLimitExceeded: {
                    setIsBlocked(true);
                    pushNotification({
                        color: 'error',
                        message: buildRequestBlockedMessage('', translation.RequestBlockedReason),
                        testId: 'verify-otp-blocked',
                        overrideIconId: 'fill.symbol.triangle-exclamation',
                    });
                    break;
                }
                case ResponseCode.RequestBlocked: {
                    setIsBlocked(true);
                    pushNotification({
                        color: 'error',
                        message: buildRequestBlockedMessage(
                            translation.RequestBlockedDetail,
                            translation.RequestBlockedReason,
                            response.windowsDurationSec
                        ),
                        testId: 'request-blocked',
                        overrideIconId: 'fill.symbol.triangle-exclamation',
                    });
                    break;
                }
                case ResponseCode.InvalidCredentials:
                case ResponseCode.Unauthenticated: {
                    pushNotification({
                        color: 'error',
                        message: translation.SessionExpired,
                        testId: 'session-expired',
                        overrideIconId: 'fill.symbol.triangle-exclamation',
                    });
                    return false;
                }
                case ResponseCode.IncorrectPin: {
                    pushNotification({
                        color: 'error',
                        message: translation.IncorrectPin,
                        testId: 'incorrect-pin',
                        overrideIconId: 'fill.symbol.triangle-exclamation',
                    });
                    return false;
                }
                default: {
                    pushNotification({
                        color: 'error',
                        message: translation.TechnicalDifficulty,
                        testId: 'technical-difficulty',
                        overrideIconId: 'fill.symbol.triangle-exclamation',
                    });
                    return false;
                }
            }
        };

        const onInit = async () => {
            const response = await loginService.scaInit({ mfaOperation: getMfaOperationCode() });
            return response;
        };

        const onPasskeyAuth = (responseCode: ResponseCode) => {
            handlePasskeyAuthResponse(
                responseCode,
                pushNotification as PushNotificationFn,
                {
                    TechnicalDifficulty: translation.TechnicalDifficulty,
                    SessionExpired: translation.SessionExpired,
                },
                () => sdkEffect.onWalletAuthenticated(true)
            );
        };

        const walletProps: WalletContextProps = {
            walletStorage,
            onSubmit,
            onResendOtp,
            onPinSubmit,
            onPasskeyAuth,
            onInit,
        };

        return (
            <WalletContext.Provider value={walletProps}>
                <MemoizedComponent {...props} />
            </WalletContext.Provider>
        );
    };

    ComponentWithWalletAuthContext.displayName = `${Component.displayName || 'Component'}WithWalletScaContext`;
    return ComponentWithWalletAuthContext;
}
